import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Text, CustomCheckbox, useText } from '@src/components';
import { Row, Col, Button, Modal, notification } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
  getUpdateVehiclesUsableDays,
  resetSelectedRowKeys,
} from '@src/store/slice/vehicleManagementStore/vehicleManagementSlice';
import { useDaysOfWeek } from '@src/hooks/useDaysOfWeek';

const generateFuelDaysString = (selectedDays, daysOfWeek) => {
  return daysOfWeek.map((day) => (selectedDays.includes(day) ? '1' : '0')).join('');
};

const AddVehicleUsableDaysModal = ({
  isVisible,
  handleOk,
  handleCancel,
  vehicleDetailIds,
  plate,
}) => {
  const dispatch = useDispatch();
  const [api, contextHolder] = notification.useNotification();
  const { loading: loadingVehicleUsableDays } = useSelector((state) => state.vehicleManagement);
  const transactionSuccessfullyCompleted2 = useText('transactionSuccessfullyCompleted2');
  const addDayRestriction = useText('addDayRestriction');
  const [checkAll, setCheckAll] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [limitDetails, setLimitDetails] = useState({});

  // The selected days are kept as the (localized) labels themselves, so they have to be moved
  // over to the new labels when the language is switched - otherwise the buttons look unselected
  // and the usable-days mask is rebuilt without them.
  const daysOfWeek = useDaysOfWeek((remapDays) => {
    setSelectedDays((current) => remapDays(current));
    setLimitDetails((current) =>
      current?.selectedDays
        ? { ...current, selectedDays: remapDays(current.selectedDays) }
        : current,
    );
  });

  const handleCheckAllChange = (e) => {
    const checked = e.target.checked;
    const allDays = daysOfWeek;
    const selectedDays = checked ? allDays : [];

    const fuelDaysString = generateFuelDaysString(selectedDays, daysOfWeek);

    setCheckAll(checked);
    setSelectedDays(selectedDays);
    setLimitDetails({
      checkAll: checked,
      selectedDays: selectedDays,
      fuelDays: fuelDaysString,
    });
  };

  const handleDayChange = (day) => {
    const updatedDays = selectedDays.includes(day)
      ? selectedDays.filter((d) => d !== day)
      : [...selectedDays, day];

    const fuelDaysString = generateFuelDaysString(updatedDays, daysOfWeek);

    setSelectedDays(updatedDays);
    setCheckAll(updatedDays.length === 7);
    setLimitDetails({
      selectedDays: updatedDays,
      fuelDays: fuelDaysString,
      checkAll: updatedDays.length === 7,
    });
  };

  const onOk = async () => {
    try {
      const request = {
        vehicleDetailIds: vehicleDetailIds,
        usableDays: limitDetails.fuelDays,
        plate,
      };
      const response = await dispatch(getUpdateVehiclesUsableDays({ request })).unwrap();

      if (response.isSuccess) {
        api.success({
          message: addDayRestriction,
          description: transactionSuccessfullyCompleted2,
          duration: 1,
        });
      }
      handleOk();
      setSelectedDays([]);
      setLimitDetails({});
      setCheckAll(false);
      dispatch(resetSelectedRowKeys());
    } catch (error) {
      console.log('Validate Failed:', error);
    }
  };

  const onCancel = () => {
    setSelectedDays([]);
    setCheckAll(false);
    setLimitDetails({});
    handleCancel();
  };

  return (
    <div className="add-vehicle-modal" data-testid="add-vehicle-model-detail-id">
      {contextHolder}
      <Modal
        title={
          <span className="custom-modal-title">
            <Text t="addDayRestriction" />
          </span>
        }
        visible={isVisible}
        onOk={onOk}
        onCancel={onCancel}
        width={1200}
        className="custom-modal"
        footer={[
          <Button key="back" onClick={onCancel}>
            <Text t="close" />
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loadingVehicleUsableDays.vehiclesUsableDays}
            disabled={loadingVehicleUsableDays.vehiclesUsableDays}
            onClick={onOk}
          >
            <Text t="cardParameterSave" />
          </Button>,
        ]}
      >
        <div style={{ padding: '30px 15px' }}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <div className="add-car-set-limit-type">
                <div className="responsive-container">
                  <div className="top-row">
                    <div className="question">
                      <Text t="whichDaysCanThisVehiclePurchaseFuel" />
                    </div>
                    <div className="checkbox-container">
                      <CustomCheckbox checked={checkAll} onChange={handleCheckAllChange}>
                        <span className="address-form-item">
                          <Text t="allDays" />
                        </span>
                      </CustomCheckbox>
                    </div>
                  </div>
                  <div className="input-container">
                    {daysOfWeek.map((day) => (
                      <Button
                        key={day}
                        type={selectedDays.includes(day) ? 'primary' : 'default'}
                        onClick={() => handleDayChange(day)}
                      >
                        {day}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Modal>
    </div>
  );
};

AddVehicleUsableDaysModal.propTypes = {
  isVisible: PropTypes.bool,
  handleOk: PropTypes.func,
  handleCancel: PropTypes.func,
  vehicleDetailIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  plate: PropTypes.string,
};

export default AddVehicleUsableDaysModal;
